import { UserChild } from '@kcftech/prometheustypescriptapiclient';
import { InfiniteScroll } from 'components/infinite_scroll/InfiniteScroll';
import { StatelessPopup } from 'components/popup/stateless_popup/StatelessPopup';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { Tag } from 'components/tag/Tag';
import {
    EmailColumn,
    FirstNameColumn,
    LastNameColumn,
    SubscribersGridContainer,
    SubscribersList,
    UsersHeader,
} from 'components/users/styled';
import { UsersList } from 'components/users/UsersList';
import { InfiniteDataSource } from 'custom_hooks/data_hooks/data_source/useInfiniteDataSource';
import { PopupReturn, usePopup } from 'custom_hooks/usePopup';
import { useThemeValues } from 'styles/theme';

export type GroupSubscribersProps = Readonly<{
    popupElement: (popupProps: PopupReturn) => JSX.Element;
    usersCount: number;
}>;

export function GroupSubscribers({ popupElement, usersCount }: GroupSubscribersProps) {
    const popupProps = usePopup();
    const { isOpen, open, triggerRef } = popupProps;

    return (
        <div>
            {usersCount > 0 ? (
                <Tag onSelect={open} selected={isOpen}>
                    <span ref={triggerRef}>{`${usersCount} ${
                        usersCount > 1 ? `Subscribers` : `Subscriber`
                    }`}</span>
                </Tag>
            ) : (
                <Tag>No Subscribers</Tag>
            )}
            {isOpen && popupElement(popupProps)}
        </div>
    );
}

export function GroupSubscribersPopup({
    users,
    dataSource,
    popupProps,
}: {
    users: UserChild[];
    dataSource: Pick<
        InfiniteDataSource<UserChild, unknown>,
        'loadNext' | 'isLoading' | 'hasNext'
    >;
    popupProps: PopupReturn;
}) {
    const { popupText } = useThemeValues();
    const { close, triggerRef, popupRef } = popupProps;
    const { loadNext, isLoading, hasNext } = dataSource;
    return (
        <StatelessPopup
            noPadding
            ref={popupRef}
            anchor={triggerRef.current}
            anchorAlign={{
                horizontal: 'left',
                vertical: 'bottom',
            }}
            popupAlign={{
                horizontal: 'right',
                vertical: 'top',
            }}
            popupClassName="modal-popup"
            onClose={close}
        >
            <SubscribersGridContainer>
                <UsersHeader $color={popupText}>
                    <FirstNameColumn>First Name</FirstNameColumn>
                    <LastNameColumn>Last Name</LastNameColumn>
                    <EmailColumn>Email</EmailColumn>
                </UsersHeader>
                <SubscribersList $maxHeight="30rem">
                    <InfiniteScroll
                        onLoadMore={loadNext}
                        isLoading={isLoading}
                        enabled={hasNext ?? false}
                    >
                        <SpinnerArea isLoading={isLoading}>
                            {users.map((user) => (
                                <UsersList
                                    key={user.UserId}
                                    color={popupText}
                                    user={user}
                                />
                            ))}
                        </SpinnerArea>
                    </InfiniteScroll>
                </SubscribersList>
            </SubscribersGridContainer>
        </StatelessPopup>
    );
}
